<template>
  <VPopper
    ref="popper"
    :class="className"
    :click-register-on="['.v-input__inner']"
    :close-on-click-reference="!noDropdown"
    :lazy
    mount-to="#v-poppers"
    :offset="5"
    placement="bottom-start"
    :popper-class
    :tabindex="focused ? 0 : -1"
    transition="v-zoom-in-top"
    :trigger="isDisabled ? 'disabled' : 'focus'"
    @blur="focused = false"
    @hide="onHidePopper"
    @show="onShowPopper"
  >
    <template #reference>
      <VInput
        ref="input"
        class="v-select__input"
        :class="{ 'has-dropdown': !noDropdown, 'is-multiple': multiple, 'has-suggestions': remainingSuggestions.length > 0, 'no-wrap': noWrap }"
        :clearable
        :disabled="isDisabled"
        :error
        :focusable="false"
        :help
        hide-number-controls
        :label
        :loading
        :minimal
        :model-value="inputValue"
        :optional
        :placeholder="inputValue ? '' : label ? label : placeholder"
        :prop
        readonly
        :required
        :show-character-limit
        :size
        :status
        :type
        @clear="clear"
        @mouseup="filter?.input?.focus()"
      >
        <template #input="{ style }">
          <div class="v-input__inner" :style>
            <slot :disabled="isDisabled" name="value">
              <div v-if="!noWrap && multiple && model?.length > 0 && Array.isArray(displayValue)" class="v-select__tags">
                <!-- Selected options -->
                <VTag
                  v-for="(item, index) in displayValue.slice(0, maxCollapseTags)"
                  :key="index"
                  :class="{ 'is-focused': pressedFilterBackspaceBefore && index === displayValue.length - 1 }"
                  :closable="!isDisabled"
                  size="small"
                  type="secondary"
                  @close="removeItemFromValue(index)"
                >
                  {{ item }}
                </VTag>
                <VTag v-if="maxCollapseTags !== undefined && displayValue.length > maxCollapseTags" size="small" type="secondary">
                  + {{ displayValue.length - maxCollapseTags }}
                </VTag>
              </div>
              <div v-else>
                {{ Array.isArray(displayValue) ? displayValue.join(', ') : displayValue }}
              </div>
            </slot>

            <span v-if="filterable && !isDisabled && noDropdown" class="v-select__filter--inline">
              <VInput
                ref="filter"
                v-model="filterValue"
                class="v-select__filter-input"
                :focusable="false"
                :maxlength
                minimal
                :placeholder="filterPlaceholder"
                @keydown="onFilterInputKeyDown"
                @update:model-value="filterInput"
              />
            </span>
          </div>
        </template>

        <template v-if="!noDropdown && (minimal || !isDisabled)" #suffix-start>
          <VIcon class="v-input__icon" icon="ChevronDown" />
        </template>

        <template #below>
          <VTransitionCollapse lazy :visible="remainingSuggestions.length > 0">
            <div class="v-select__suggestions" @mouseup.stop>
              <div class="v-select__suggestions-label">
                {{ i18n.suggested.replace('$label', (label ? label : placeholder).toLowerCase()) }}
                <VButton v-if="showAddAllSuggestions && multiple" :label="i18n.addAll" style="padding-left: 10px; font-size: 14px" type="link" @click="addAllSuggestions()" />
              </div>
              <VTag v-for="item in limitedRemainingSuggestions" :key="item" size="small" @click="onClickSuggestion(item)">
                <slot name="suggestion" :suggestion="item">
                  <span style="margin-left: -2px">&nbsp;</span>
                  <VIcon icon="Plus" :size="10" :stroke-width="0.5" style="margin-right: 3px" />
                  {{ item }}
                </slot>
              </VTag>
              <VTag v-if="showMoreSuggestionsLimit && (showMore || remainingSuggestions.length > limitedRemainingSuggestions.length)" size="small" @click="showMore = !showMore">
                {{ showMore ? i18n.showLess : i18n.showMore }}
                <VIcon :icon="showMore ? 'Minus' : 'Plus'" :size="10" :stroke-width="0.5" style="margin-left: 3px" />
              </VTag>
            </div>
          </VTransitionCollapse>
        </template>
      </VInput>
    </template>

    <ul
      class="v-select-options"
      :class="{ 'v-select-options--filterable': filterable && !isDisabled && !noDropdown, 'has-visible-options': visibleOptions.length > 0 }"
      :style="{ width: `${optionsWidth}px` }"
    >
      <li v-if="filterable && !isDisabled && !noDropdown" class="v-select__filter">
        <VInput
          ref="filter"
          v-model="filterValue"
          class="v-select__filter-input"
          :maxlength
          minimal
          :placeholder="filterPlaceholder"
          @keydown="onFilterInputKeyDown"
          @update:model-value="filterInput"
        />
      </li>
      <VOption v-if="createNewOptionVisible" is-filter-option :label="filterValue" :value="filterValue" />

      <li v-if="filterLoading" style="padding: 10px 0">
        <VLoadingDots :size="6" />
      </li>

      <li v-else-if="filterValue && optionsEmpty" class="v-option-group__title">
        <slot name="options-empty">{{ i18n.noResultsFoundForYourInput }}</slot>
      </li>

      <li v-else-if="optionsEmpty" class="v-option-group__title">
        <slot name="options-empty">{{ i18n.noResultsFound }}</slot>
      </li>

      <div class="v-select-options__items">
        <slot />
      </div>
    </ul>
  </VPopper>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, provide, reactive, ref, type UnwrapNestedRefs, useTemplateRef, watch } from 'vue'

import { useClassName } from '../../composables/useClassName.ts'
import { useResizeObserver } from '../../composables/useResizeObserver.ts'
import { delay } from '../../utils/helpers.ts'
import { i18nSelect as i18n } from '../../utils/i18n.ts'
import { $formKey, $selectKey } from '../../utils/injections.ts'
import VIcon from '../Icon/VIcon.vue'
import VButton from '../VButton.vue'
import VInput, { type Props as InputProps } from '../VInput.vue'
import VLoadingDots from '../VLoadingDots.vue'
import VPopper from '../VPopper.vue'
import VTag from '../VTag.vue'
import VTransitionCollapse from '../VTransitionCollapse.vue'
import VOption, { type Option } from './VOption.vue'

export interface Props extends InputProps {
  defaultFirstOption?: boolean
  multipleLimit?: number
  showMoreSuggestionsLimit?: number
  filterable?: boolean
  multiple?: boolean
  allowCreate?: boolean
  noDropdown?: boolean
  showAddAllSuggestions?: boolean
  filterPlaceholder?: string
  onFilterInput?: (value: string) => unknown
  minOptionsWidth?: number
  ignoreClickOnSelectedOption?: boolean
  /** When the value is an object, you can specify a key by which to identify the object. (e.g. 'id') default: `id`. */
  valueKey?: string
  maxCollapseTags?: number
  noWrap?: boolean
}

const {
  type = 'text',
  placeholder = '',
  label = '',
  disabled = null,
  clearable = false,
  maxlength = undefined,
  status = '',
  error = '',
  help = '',
  suggestions = [],
  prop = '',
  required = null,
  minimal = false,
  showCharacterLimit = false,
  optional = false,
  focusOnMount = false,
  loading = false,
  defaultFirstOption = true,
  multipleLimit = 0,
  showMoreSuggestionsLimit = 0,
  filterable = false,
  multiple = false,
  allowCreate = false,
  noDropdown = false,
  showAddAllSuggestions = false,
  filterPlaceholder = '',
  onFilterInput = () => true,
  minOptionsWidth = 200,
  ignoreClickOnSelectedOption = false,
  valueKey = 'id',
  maxCollapseTags = undefined,
  noWrap = false,
} = defineProps<Props>()

const model = defineModel<any>({ default: undefined })
const filterValue = defineModel<string>('filterValue', { default: '' })

const emit = defineEmits<{
  visibleChange: [value: boolean]
}>()

const slots = defineSlots<{
  value?: (props: any) => any
  default?: (props: any) => any
  'options-empty'?: (props: any) => any
  suggestion?: (props: { suggestion: string }) => any
}>()

const form = inject($formKey, undefined)

const optionsWidth = ref(0)
const activeOption = ref<undefined | UnwrapNestedRefs<Option>>() // The option that is currently activated by using key down events
const pressedFilterBackspaceBefore = ref(false)
const displayValue = ref<string | string[]>('')
const focused = ref(false)
const filterLoading = ref(false)
const options = reactive<Record<number, UnwrapNestedRefs<Option>>>({})
const showMore = ref(false)
const popper = useTemplateRef('popper')
const input = useTemplateRef('input')
const filter = useTemplateRef('filter')

const isDisabled = computed(() => !!(disabled || (disabled === null && form?.props?.disabled)))
const lazy = computed(() => model.value === null || (multiple && Array.isArray(model.value) && model.value.length === 0))
const optionsEmpty = computed(() => Object.values(options).every((option) => !option.visible))
const optionsLength = computed(() => Object.keys(options).length)
const visibleOptions = computed(() => Object.values(options).filter((option) => option.visible))

const className = useClassName(
  'v-select',
  computed(() => [label, placeholder]),
)
const popperClass = useClassName(
  'v-popper--select',
  computed(() => [label, placeholder, multiple ? 'multiple' : '']),
  { 'v-popper--hidden': noDropdown, 'has-label': label },
)

const inputValue = computed(() => {
  if (displayValue.value.length > 0) {
    return '.'
  }

  if (noDropdown && filterValue.value) {
    return '.'
  }

  return ''
})

const remainingSuggestions = computed(() => {
  return suggestions.filter((suggestion) => {
    if (!model.value) {
      return true
    }
    if (!Array.isArray(model.value)) {
      return suggestion === model.value
    }

    return !model.value.includes(suggestion)
  })
})

const limitedRemainingSuggestions = computed(() => {
  return showMoreSuggestionsLimit && !showMore.value ? remainingSuggestions.value.slice(0, showMoreSuggestionsLimit) : remainingSuggestions.value
})

const createNewOptionVisible = computed(() => {
  if (!allowCreate || !filterValue.value) {
    return false
  }

  const optionsWithSameLabel = visibleOptions.value.filter((option) => !option.isFilterOption && option.label === filterValue.value)

  return optionsWithSameLabel.length === 0
})

watch(
  () => model.value,
  async () => {
    await nextTick()
    await nextTick()
    updateDisplayValue()
  },
  { immediate: true, deep: true },
)

/**
 * This cannot be a computed because vue is still patching option when value is changed.
 * Which means that defaultSlots.componentInstance is undefined.
 * We need to use $nextTick to wait for the patching to be done.
 */
watch(
  () => optionsLength.value,
  () => updateDisplayValue(),
)

onBeforeUnmount(() => document.removeEventListener('keydown', onKeyDown))

onMounted(() => {
  if (popper.value?.reference) {
    useResizeObserver(popper.value?.reference, onReferenceResize)
  }

  if (focusOnMount) {
    popper.value?.show()
  }
})

function updateDisplayValue() {
  const value = model.value
  if (multiple) {
    displayValue.value = Array.isArray(value) ? value.map((item) => formatValue(item)) : []
  } else {
    displayValue.value = formatValue(value)
  }
}

function formatOptionValue(option: UnwrapNestedRefs<Option>, value: any): string | undefined {
  if (valueMatch(option.value, value)) {
    const label = option.label

    if (!label && value && typeof value === 'object') {
      return JSON.stringify(value)
    }

    return label ?? value?.toString() ?? ''
  }
}

function formatValue(value: any) {
  for (const option of Object.values(options)) {
    const formatted = formatOptionValue(option, value)
    if (formatted !== undefined) {
      return formatted
    }
  }

  if (lazy.value && slots.default) {
    const defaultSlots = slots.default(undefined)

    for (const child of defaultSlots ?? []) {
      if (child.props) {
        const formatted = formatOptionValue(child.props, value)
        if (formatted !== undefined) {
          return formatted
        }
      }
    }
  }

  if (value && typeof value === 'object') {
    return JSON.stringify(value)
  }

  return value?.toString() ?? ''
}

async function clear() {
  updateFilterValue('')
  await onFilterInput('')

  if (multiple) {
    model.value = []
  } else if (type === 'number') {
    model.value = null
  } else {
    model.value = ''
  }
}

async function filterInput(value: string) {
  if (noDropdown) {
    activeOption.value = undefined
  }
  filterLoading.value = true
  await onFilterInput(value)
  filterLoading.value = false

  if (value) {
    // Backspace state should be not be true when value is not empty
    pressedFilterBackspaceBefore.value = false
  }

  if (defaultFirstOption && !activeOption.value?.visible) {
    activeOption.value = visibleOptions.value[0]
  }
}

/**
 * When pressing backspace, the last selected value should be 'focused'
 * When backspace is then pressed again, the last selected value should be removed.
 * This only applies when in multiple mode.
 */
function onFilterInputKeyDown(event: KeyboardEvent) {
  if (!multiple || !Array.isArray(model.value) || event.key !== 'Backspace' || filterValue.value !== '') {
    return
  }

  if (!pressedFilterBackspaceBefore.value) {
    pressedFilterBackspaceBefore.value = true

    return
  }

  const lastSelectedOption = visibleOptions.value.findLast((option) => option.selected)
  if (lastSelectedOption?.props.disabled) {
    return
  }

  pressedFilterBackspaceBefore.value = false
  const value = [...model.value]
  value.pop()
  model.value = value
}

function onShowPopper() {
  if (defaultFirstOption && !activeOption.value) {
    activeOption.value = visibleOptions.value[0]
  }

  filter.value?.input?.focus()
  emit('visibleChange', true)
  document.addEventListener('keydown', onKeyDown)
}

async function onHidePopper() {
  pressedFilterBackspaceBefore.value = false

  if (filterValue.value && noDropdown && activeOption.value && !activeOption.value.selected) {
    activeOption.value.onClick()
  }

  document.removeEventListener('keydown', onKeyDown)
  emit('visibleChange', false)
  await delay(300)
  updateFilterValue('')
  filter.value?.input?.blur()
}

function onReferenceResize({ width }: DOMRectReadOnly) {
  optionsWidth.value = width > minOptionsWidth ? width : minOptionsWidth
}

async function onKeyDown({ key }: { key: string }) {
  if (key === 'ArrowDown') {
    nextOption()
  } else if (key === 'ArrowUp') {
    previousOption()
  } else if (key === 'Enter' && activeOption.value) {
    if (!noDropdown || !activeOption.value.selected) {
      activeOption.value.onClick()
      // Reset the activeOption, otherwise, the no longer used activeOption remains in memory
      // and typing won't trigger a new option with a new value
      if (noDropdown) {
        activeOption.value = undefined
      }
    } else {
      updateFilterValue('')
    }
  } else if (key === 'Escape' || key === 'Tab') {
    popper.value?.reference?.focus()
    await delay(1)
    popper.value?.hide()
  }
}

function nextOption() {
  const options = visibleOptions.value

  if (!activeOption.value) {
    activeOption.value = options[0]
  }

  const index = options.indexOf(activeOption.value)

  if (index >= 0 && index !== options.length - 1) {
    activeOption.value = options[index + 1]
  } else {
    activeOption.value = options[0]
  }
}

function previousOption() {
  const options = visibleOptions.value

  if (!activeOption.value) {
    activeOption.value = options[0] ?? null
  }

  const index = options.indexOf(activeOption.value)

  if (index > 0) {
    activeOption.value = options[index - 1]
  } else {
    activeOption.value = options[options.length - 1]
  }
}

function updateFilterValue(value: string) {
  filterValue.value = value
}

function removeItemFromValue(displayIndex: number) {
  if (!Array.isArray(model.value)) {
    return
  }

  const value = model.value.slice()
  value.splice(displayIndex, 1)
  model.value = value

  if (prop) {
    form?.validateOneProp(prop)
  }
}

function onClickSuggestion(suggestion: string) {
  if (multiple) {
    const value = Array.isArray(model.value) ? model.value.slice() : []
    value.push(suggestion)

    model.value = value
  } else {
    model.value = suggestion
  }
}

function addAllSuggestions() {
  const value = Array.isArray(model.value) ? model.value.slice() : []
  value.push(...remainingSuggestions.value)

  model.value = value
}

function focus() {
  focused.value = true
  input.value?.focus()
}

function valueMatch(value1: unknown, value2: unknown): boolean {
  if (value1 === value2) return true

  if (value1 !== null && value2 !== null && typeof value1 === 'object' && typeof value2 === 'object') {
    if (valueKey && valueKey in value1 && valueKey in value2) {
      // @ts-expect-error - We checked if valueKey is in value1 & value2.
      return value1[valueKey] === value2[valueKey]
    }
    try {
      return JSON.stringify(value1) === JSON.stringify(value2)
    } catch {
      return false
    }
  }

  return false
}

provide($selectKey, {
  multiple,
  multipleLimit,
  prop,
  filterable,
  ignoreClickOnSelectedOption,
  noDropdown,
  model,
  activeOption,
  filterValue,
  options,
  popper,
  input,
  focus,
  emit,
  valueMatch,
})

defineExpose({
  clear,
  popper,
  focus,
  filter,
  filterInput,
  input,
  activeOption,
  options,
  nextOption,
  previousOption,
  removeItemFromValue,
  pressedFilterBackspaceBefore,
})
</script>

<style>
.v-select__input {
  &:not(.is-disabled) {
    cursor: pointer;
  }

  &:not(.has-label) {
    .v-input__inner,
    .v-input__value {
      padding: 2px 10px;
    }

    &.has-suffix .v-input__value {
      padding-right: 30px;
    }
  }

  &.is-minimal.focused .v-input__inner {
    border-bottom: none;
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
  }

  &.has-suggestions {
    .v-input__inner {
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .v-input__suffix {
      border-bottom: none;
      border-bottom-right-radius: 0;
    }
  }

  .v-input__value {
    overflow: hidden;
    height: auto;
    flex-wrap: auto;
    cursor: pointer;
  }

  &.no-wrap .v-input__inner > div {
    position: relative;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;

    /* Gradient to 0 opacity */
    &:after {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      width: 20px;
      height: 100%;
      background: linear-gradient(to right, rgba(255, 255, 255, 0), #fff);
    }
  }

  &.no-wrap.is-disabled .v-input__inner > div {
    &:after {
      background: linear-gradient(to right, rgba(255, 255, 255, 0), #fafafa);
    }
  }
}

ul.v-select-options {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  list-style-type: none;
  margin: 0;
  min-height: 40px;
  margin-top: -1px;

  .v-select-options__items {
    overflow-y: scroll;
    max-height: 400px;
  }

  &.has-visible-options .v-select-options__items {
    padding: 10px 0;
  }
}

.v-select {
  position: relative;
  outline: none;

  &.v-popper--active {
    .v-select__input:not(.is-minimal) .v-input__inner,
    .v-select__input:not(.is-minimal) .v-input__suffix {
      border-color: var(--color-primary);
    }

    .v-input__icon.v-icon-chevrondown {
      transform: rotate(180deg);
    }
  }
}

.v-popper--select[data-popper-placement^='top']:not(.v-popper--hidden) {
  &.v-zoom-in-top-enter-active,
  &.v-zoom-in-top-leave-active {
    transform-origin: center bottom;
  }
}

.v-select__tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 4px;

  .v-tag {
    margin-top: 4px;
    overflow-wrap: anywhere;
  }
}

.v-select__filter {
  border-bottom: 1px solid var(--color-grey-8);
}

.v-select .v-select__filter--inline {
  display: none;
  position: relative;
  width: 100%;

  /* When filter input is below v-select__tags. */
  &:not(:first-child) {
    margin-top: -3px;
    margin-bottom: 3px;
  }

  .v-input__inner {
    border: none;
    padding: 0 !important;
    margin-top: -3px;
    margin-bottom: -3px;
  }
}

.v-select.v-popper--active .v-select__filter--inline {
  display: inline-block;
}

.v-select__input.v-input {
  &.is-minimal .v-input__inner {
    width: auto;
    padding: 0;
  }

  .v-input__inner {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &.has-label {
    .v-select__tags {
      margin-right: 5px;
    }

    .v-select__filter--inline {
      .v-input__inner {
        min-height: 36px;
        padding: 0;
      }
    }
  }
}

.v-select__suggestions {
  background-color: var(--color-grey-4);
  border: var(--border);
  border-bottom-left-radius: var(--border-radius);
  border-bottom-right-radius: var(--border-radius);
  padding: 16px 20px 12px 20px;
  pointer-events: all;
  cursor: default;

  .v-select__suggestions-label {
    font-size: 14px;
    line-height: 1;
    margin-bottom: 10px;
  }

  .v-tag {
    cursor: pointer;
    margin-bottom: 4px;
  }
}
</style>
